import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IFormValues, TBenefitProgram } from '../../../types';
import { DEFAULT_DOWN_PAYMENT_PERCENTAGE, DOWN_PAYMENT_VALUES } from '../constants';
import { calculateValueFromPercentage } from '../../../utils';
import { EFieldName } from '../../../constants';
import { debounce } from '@cian/ui-kit/internal/utils';
import { IFetchOffersProps } from '../../../hooks/useGetOffers';
import { useSelector } from 'react-redux';
import { selectFormDefaults } from '../../../selectors/calculator';
import { useTracking } from 'shared/mortgageMiniCalculator/utils/tracking/useTracking';
import { ETypeChangeInput } from '../../../utils/tracking/types';
import { useApplicationContext } from 'shared/common/utils/applicationContext';
import { ERuntimeSettings } from 'shared/common/types/runtimeSettings';
import { mapFieldNameToAnalyticsFieldName } from '../../../utils/mapFieldNameToAnalyticsFieldName';

export const useFormCalculator = (fetchOffers: (params: IFetchOffersProps) => void) => {
  const initialFormValues = useSelector(selectFormDefaults);
  const { trackEditFieldValues } = useTracking();

  const [percentDownPayment, setPercentDownPayment] = useState(() => {
    return (initialFormValues.downPayment / initialFormValues.objectCost) * 100 || DEFAULT_DOWN_PAYMENT_PERCENTAGE;
  });

  const { config } = useApplicationContext();
  const isLifeInsuranceFieldEnabled = config.get<boolean>(ERuntimeSettings.lifeInsuranceFieldEnabled) ?? false;
  const isProgramFilterFieldEnabled = config.get<boolean>(ERuntimeSettings.programFilterFieldEnabled) ?? false;

  const {
    setValue,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<IFormValues>({
    defaultValues: initialFormValues,
  });

  const { downPayment, objectCost, loanTermYears, lifeInsurance, benefitProgram } = watch();

  const onSubmit = useCallback(
    async (data: IFormValues) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { benefitProgram, ...restData } = data;
      const submitData = isProgramFilterFieldEnabled ? data : restData;
      fetchOffers(submitData);
    },
    [fetchOffers, isProgramFilterFieldEnabled],
  );

  const downPaymentOptions = useMemo(() => {
    return DOWN_PAYMENT_VALUES.map(option => {
      return {
        value: calculateValueFromPercentage(objectCost, Number(option)),
        label: `${option}%`,
        dataTestId: `down-payment-${option}`,
      };
    });
  }, [objectCost]);

  useEffect(() => {
    setValue(EFieldName.ObjectCost, initialFormValues.objectCost);
    setValue(EFieldName.DownPayment, initialFormValues.downPayment);
    setValue(EFieldName.loanTermYears, initialFormValues.loanTermYears);
    // TODO: удалить после релиза выбора страхования жизни
    setValue(EFieldName.lifeInsurance, isLifeInsuranceFieldEnabled);
    if (isProgramFilterFieldEnabled) {
      const benefitProgramValue = initialFormValues.benefitProgram || 'noProgram';
      setValue(EFieldName.benefitProgram, benefitProgramValue);
    }
  }, [initialFormValues, setValue, isLifeInsuranceFieldEnabled, isProgramFilterFieldEnabled]);

  useEffect(() => {
    if (initialFormValues.downPayment && initialFormValues.objectCost) {
      const newPercent = (initialFormValues.downPayment / initialFormValues.objectCost) * 100;
      setPercentDownPayment(newPercent);
    }
  }, [initialFormValues.downPayment, initialFormValues.objectCost]);

  useEffect(() => {
    setValue(EFieldName.DownPayment, calculateValueFromPercentage(objectCost, percentDownPayment));
  }, [objectCost, percentDownPayment, setValue]);

  const { debouncedSubmit, debouncedTrack } = useMemo(() => {
    return {
      debouncedSubmit: debounce(handleSubmit(onSubmit), 500),
      debouncedTrack: debounce(trackEditFieldValues, 500),
    };
  }, [handleSubmit, onSubmit, trackEditFieldValues]);

  const getHandlerByFieldName = useCallback(
    (fieldName: EFieldName, typeChange: ETypeChangeInput) => async (value: number | string) => {
      if (fieldName === EFieldName.lifeInsurance) {
        setValue(fieldName, !!value);
      } else if (fieldName === EFieldName.benefitProgram) {
        setValue(fieldName, value as TBenefitProgram);
      } else {
        setValue(fieldName, value as number);
      }

      if (fieldName === EFieldName.DownPayment) {
        setPercentDownPayment(((value as number) / objectCost) * 100);
      }

      const trackingFieldName = mapFieldNameToAnalyticsFieldName[fieldName];

      if (isValid) {
        await debouncedSubmit();
        debouncedTrack(typeChange, value, trackingFieldName);
      }
    },
    [setValue, objectCost, isValid, debouncedSubmit, debouncedTrack],
  );

  return {
    objectCost,
    downPayment,
    loanTermYears,
    downPaymentOptions,
    getHandlerByFieldName,
    isValid,
    lifeInsurance,
    benefitProgram,
  };
};
